import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api'; 

import { COUNTRY_OPTIONS } from '../utils/countryDialCodes';
const COUNTRY_CODES = Array.from(new Set(COUNTRY_OPTIONS.map(c => c.dialCode)));

// Validation schema
const validationSchema = yup.object({
  candidateName: yup.string().required('Candidate name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  countryCode: yup.string().oneOf(COUNTRY_CODES).required('Country code is required'),
  mobileNumberLocal: yup
    .string()
    .matches(/^\d{1,15}$/, 'Enter up to 15 digits')
    .required('Mobile number is required'),
  department: yup.string().required('Department is required'),
  designation: yup.string().required('Designation is required'),
  employeeGrade: yup.string().required('Employee grade is required'),
  dateOfJoining: yup.date().required('Date of joining is required'),
  placeOfJoining: yup.string().required('Place of joining is required'),
});

const ITOnboardingForm = () => {
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(`${API_URL}/departments/public`);
        const data = await res.json();
        setDepartments(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load departments', e);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);
  const formik = useFormik({
    initialValues: {
      candidateName: '',
      email: '',
      countryCode: '+91',
      mobileNumberLocal: '',
      mobileNumber: '',
      department: '',
      designation: '',
      employeeGrade: '',
      dateOfJoining: '',
      placeOfJoining: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Compose E.164-like number and validate max length (15 digits)
        const localDigits = (values.mobileNumberLocal || '').replace(/\D/g, '');
        const cc = values.countryCode || '+91';
        const combinedDigits = `${cc.replace('+','')}${localDigits}`;
        if (combinedDigits.length > 15) {
          throw new Error('Mobile number must be at most 15 digits including country code');
        }
        const payload = {
          ...values,
          mobileNumber: `+${combinedDigits}`,
        };
        delete payload.mobileNumberLocal;
        delete payload.countryCode;
        const response = await fetch(`${API_URL}/it-onboarding/it-form`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (response.ok) {
          toast.success('Form submitted successfully!');
          resetForm();
        } else {
          toast.error(data.message || 'Submission failed');
        }
      } catch (err) {
        console.error(err);
        toast.error('An error occurred while submitting the form.');
      }
    },
  });
  
  const [searchParams] = useSearchParams();
  const emailFromURL = searchParams.get('email');
  
  
  useEffect(() => {
    if (emailFromURL) {
      formik.setFieldValue('email', emailFromURL);
    }
  }, [emailFromURL]);

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto',  mt: 5, px: { xs: 3, sm: 4 } }}>
      <Typography variant="h5" sx={{mb: 1,fontWeight: 600,color: '#333',fontSize: { xs: '1.2rem', sm: '1.5rem' },}}
      align="center">
       IT - Employee Onboarding Details
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={3}>
          <TextField
            label="Candidate Name"
            name="candidateName"
            value={formik.values.candidateName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.candidateName && Boolean(formik.errors.candidateName)}
            helperText={formik.touched.candidateName && formik.errors.candidateName}
            fullWidth
            required
          />

          <TextField
            label="Email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            fullWidth
            required
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Autocomplete
              sx={{ minWidth: 260 }}
              options={COUNTRY_OPTIONS}
              getOptionLabel={(opt) => opt?.dialCode || ''}
              filterOptions={(options, { inputValue }) => {
                const q = (inputValue || '').toLowerCase();
                return options.filter(o =>
                  (o.name || '').toLowerCase().includes(q)
                  || (o.iso2 || '').toLowerCase().includes(q)
                  || (o.iso3 || '').toLowerCase().includes(q)
                  || (o.dialCode || '').includes(inputValue)
                );
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  {`${option.iso2 || option.iso3 || option.name} (${option.dialCode})`}
                </li>
              )}
              value={COUNTRY_OPTIONS.find(o => o.dialCode === formik.values.countryCode) || null}
              onChange={(_, newVal) => {
                formik.setFieldValue('countryCode', newVal?.dialCode || '+91');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Country Code" placeholder="Type country or code" />
              )}
            />
            <TextField
              label="Mobile Number"
              name="mobileNumberLocal"
              value={formik.values.mobileNumberLocal}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.mobileNumberLocal && Boolean(formik.errors.mobileNumberLocal)}
              helperText={formik.touched.mobileNumberLocal && formik.errors.mobileNumberLocal}
              fullWidth
              required
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 15 }}
            />
          </Box>

          <FormControl fullWidth required error={formik.touched.department && Boolean(formik.errors.department)}>
            <InputLabel>Department</InputLabel>
            <Select
              label="Department"
              name="department"
              value={formik.values.department}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <MenuItem value=""><em>Select department</em></MenuItem>
              {departments.map((d) => (
                <MenuItem key={d._id || d.id || d.departmentName} value={d.departmentName}>
                  {d.departmentName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.department && formik.errors.department && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {formik.errors.department}
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Designation"
            name="designation"
            value={formik.values.designation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.designation && Boolean(formik.errors.designation)}
            helperText={formik.touched.designation && formik.errors.designation}
            fullWidth
            required
          />

          <FormControl fullWidth required error={formik.touched.employeeGrade && Boolean(formik.errors.employeeGrade)}>
            <InputLabel>Employee Grade</InputLabel>
            <Select
              label="Employee Grade"
              name="employeeGrade"
              value={formik.values.employeeGrade}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <MenuItem value=""><em>Select grade</em></MenuItem>
              {Array.from({ length: 10 }, (_, i) => `L${i + 1}`).map(grade => (
                <MenuItem key={grade} value={grade}>{grade}</MenuItem>
              ))}
            </Select>
            {formik.touched.employeeGrade && formik.errors.employeeGrade && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {formik.errors.employeeGrade}
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Date of Joining"
            name="dateOfJoining"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formik.values.dateOfJoining}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.dateOfJoining && Boolean(formik.errors.dateOfJoining)}
            helperText={formik.touched.dateOfJoining && formik.errors.dateOfJoining}
            fullWidth
            required
          />

          <TextField
            label="Place of Joining"
            name="placeOfJoining"
            value={formik.values.placeOfJoining}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.placeOfJoining && Boolean(formik.errors.placeOfJoining)}
            helperText={formik.touched.placeOfJoining && formik.errors.placeOfJoining}
            fullWidth
            required
          />

          <Button type="submit" variant="contained" fullWidth sx={{backgroundColor: '#673ab7',
          '&:hover': {backgroundColor: '#5e35b1',},fontWeight: 'bold',py: 1.5,mt: 2}}>
            Submit
          </Button>

        </Stack>
      </form>
    </Box>
  );
};

export default ITOnboardingForm; 